import type { BaseFeatureConfig } from "./featuresGates";
import { EXP_EVENTS, TEAMS } from "./featuresGates";

/**
 * Parameters used in experiments.
 * `SHOW_FEATURE` is always required; add optional keys as needed.
 */
interface VariantParam<T extends string | number | boolean = string> {
  ID: string;
  VARIANTS: readonly T[];
  DEFAULT_VALUE: T;
}

type Params = {
  SHOW_FEATURE: typeof PARAMETERS.SHOW_FEATURE;
  [key: string]: string | VariantParam<string | number | boolean> | undefined;
};

// StatSig experiments
export interface ExperimentConfig extends BaseFeatureConfig {
  ID: string; // The experiment_name on Statsig.
  PARAMS: Params; // Corresponds to the parameters in Statsig.
}

// A template for creating new experiments.
//
// export const TEMPLATE: ExperimentConfig = {
//   ID: "",
//   NAME: "",
//   LINK: "",
//   TEAM: TEAMS.,
//   PARAMS: { SHOW_FEATURE: PARAMETERS.SHOW_FEATURE },
//   DESCRIPTION: "",
//   EXPIRATION: "",
// };

/**
 * For A/B Testing.
 *
 * If using variant parameters, add them here.
 * The value should be the ID of the parameter
 * exactly as set in Statsig.
 *
 * For variant group parameters (A/B/n tests), use an object with:
 *   - ID: the Statsig parameter ID
 *   - VARIANTS: array of possible values
 *   - DEFAULT_VALUE: fallback value
 */
export const PARAMETERS = {
  SHOW_FEATURE: "show_feature",
} as const;

export const EXP_SIGNUP_INPUT_EMAIL_TITLE: ExperimentConfig = {
  ID: "exp_signup_input_email_title",
  NAME: "Signup input title for email",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_signup_input_email_title",
  TEAM: TEAMS.TEAM_NEW_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION: "Changes the title for the email input",
  EXPIRATION: "2026-02-01",
};

export const EXP_CREDITS_IN_TRIAL: ExperimentConfig = {
  ID: "exp_credits_in_trial",
  NAME: "Show credits in trial",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_credits_in_trial",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Trialers can sign up for $500 worth of credits instead of '30-day free trial'. The credits also expire.",
  EXPIRATION: "2026-03-01",
};

export const EXP_ONBOARDING_INFO_CONTENT: ExperimentConfig = {
  ID: "exp_onboarding_info_content",
  NAME: "Onboarding info content",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_onboarding_info_content/",
  TEAM: TEAMS.TEAM_NEW_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Show different onboarding info screens, one that's 'normal' and the other that de-emphasizes free services",
  EXPIRATION: "2026-06-01",
};

export const EXP_MFA_SSO_IMPROVEMENTS: ExperimentConfig = {
  ID: "exp_mfa_sso_improvements",
  NAME: "MFA / SSO improvements",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_mfa_sso_improvements/setup",
  TEAM: TEAMS.TEAM_FRONTEND,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Shows the Authentication tab in the Security section and the authentication enforcement toggle on the Members page.",
  EXPIRATION: "2026-12-01",
};

export const EXP_DELETE_MEMBER_PAT_WARNING: ExperimentConfig = {
  ID: "exp_delete_member_pat_warning",
  NAME: "Delete member PAT warning",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_delete_member_pat_warning",
  TEAM: TEAMS.TEAM_FRONTEND,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Warns owners and admins that a removed member's personal access tokens (PATs) persist with project-wide permissions.",
  EXPIRATION: "2026-12-08",
};

export const EXP_BILLING_TIMELINE: ExperimentConfig = {
  ID: "exp_billing_timeline",
  NAME: "Billing timeline",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_billing_timeline",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Shows the new billing usage timeline view on the billing details page instead of the legacy snapshot.",
  EXPIRATION: "2026-12-01",
};

export const EXP_BILLING_FEEDBACK_PANEL: ExperimentConfig = {
  ID: "exp_billing_feedback_panel",
  NAME: "Billing feedback panel",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_billing_feedback_panel",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Shows a feedback banner on the Billing pages that lets users send feedback to the product team.",
  EXPIRATION: "2026-12-16",
};

export const EXP_WHATS_NEW: ExperimentConfig = {
  ID: "exp_whats_new",
  NAME: "What's new panel",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_whats_new",
  TEAM: TEAMS.TEAM_PRODUCTION_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Controls the top-nav What's new? button behavior: treatment opens an in-app side panel of recent product updates; control keeps the old behavior of opening the changelog docs in a new tab.",
  EXPIRATION: "2027-01-21",
};

export const EXP_CONNECT_PAGE_CLI_VIDEO: ExperimentConfig = {
  ID: "exp_connect_page_cli_video",
  NAME: "Connect page CLI video",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_connect_page_cli_video",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Treatment shows a single-card connect page led by a Tiger CLI onboarding video, with one 'Set up CLI' button and the web console as a secondary option; control keeps the existing multi-card connect page.",
  EXPIRATION: "2026-12-15",
  EVENTS: {
    CLICK_CONNECT_OPTION: EXP_EVENTS.CLICK_CONNECT_OPTION,
  },
};

export const EXP_CLI_ICON: ExperimentConfig = {
  ID: "exp_cli_icon",
  NAME: "Top nav CLI icon",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_cli_icon",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Displays a terminal icon linking to the CLI / MCP page in the top nav.",
  EXPIRATION: "2026-10-24",
  EVENTS: {
    CLICK_CLI_ICON: EXP_EVENTS.CLICK_CLI_ICON,
  },
};

export const EXP_SERVICE_OVERVIEW_TIGER_CLI: ExperimentConfig = {
  ID: "exp_service_overview_tiger_cli",
  NAME: "Tiger CLI on the Service Overview",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_service_overview_tiger_cli",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
  },
  DESCRIPTION:
    "Treatment adds a Tiger CLI row with a copyable `tiger service get` command and a CLI setup link to the Service Overview's Connect section, plus a Tiger CLI section in the Connect drawer; control keeps the connection string only.",
  EXPIRATION: "2027-01-31",
  EVENTS: {
    COPY_TIGER_CLI_COMMAND: EXP_EVENTS.COPY_TIGER_CLI_COMMAND,
    CLICK_CONNECT_CLI_SETUP: EXP_EVENTS.CLICK_CONNECT_CLI_SETUP,
  },
};

export const EXP_HISTORICAL_CONNECTIONS_NEW_METRICS: ExperimentConfig = {
  ID: "exp_historical_connections_new_metrics",
  NAME: "Historical connections from new metrics",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_historical_connections_new_metrics",
  TEAM: TEAMS.TEAM_PLATFORM,
  PARAMS: { SHOW_FEATURE: PARAMETERS.SHOW_FEATURE },
  DESCRIPTION:
    "Serves the connection-history graph from new, generically-queryable metrics (pg_stat_activity_count / pg_settings_*) instead of the legacy resolver.",
  EXPIRATION: "2027-01-01",
};

export const DEFAULT_CREDITS_NOTICE_TEXT =
  "You won't be charged during your trial while you have credits.";

export const EXP_CREDITS_NOTICE_SERVICE_CREATION: ExperimentConfig = {
  ID: "exp_credits_notice_service_creation",
  NAME: "Credits notice in service creation",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_credits_notice_service_creation",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: {
    SHOW_FEATURE: PARAMETERS.SHOW_FEATURE,
    DISPLAY_TEXT: "display_text",
  },
  DESCRIPTION:
    "Shows a notice at the bottom of the pricing sidebar in the service creation flow, informing trial users how many credits they have remaining and that they won't be charged while credits are available.",
  EXPIRATION: "2027-03-01",
};

export const EXP_MICROSURVEY_TEXT_RESPONSES: ExperimentConfig = {
  ID: "exp_microsurvey_text_responses",
  NAME: "Microsurvey text responses",
  LINK: "https://console.statsig.com/2aVMoalsJmTVASIsy8WxBu/experiments/exp_microsurvey_text_responses",
  TEAM: TEAMS.TEAM_BUSINESS_USER_EXPERIENCE,
  PARAMS: { SHOW_FEATURE: PARAMETERS.SHOW_FEATURE },
  DESCRIPTION:
    "Adds text boxes in the microsurvey for certain options (AI, Recommendation, Other) so users can give more detail",
  EXPIRATION: "2027-03-01",
};

// Add to the list to show for the ExperimentsModal.
export const ALL_EXPERIMENTS: ExperimentConfig[] = [
  EXP_CONNECT_PAGE_CLI_VIDEO,
  EXP_SIGNUP_INPUT_EMAIL_TITLE,
  EXP_CREDITS_IN_TRIAL,
  EXP_ONBOARDING_INFO_CONTENT,
  EXP_MFA_SSO_IMPROVEMENTS,
  EXP_DELETE_MEMBER_PAT_WARNING,
  EXP_BILLING_TIMELINE,
  EXP_BILLING_FEEDBACK_PANEL,
  EXP_WHATS_NEW,
  EXP_CLI_ICON,
  EXP_SERVICE_OVERVIEW_TIGER_CLI,
  EXP_HISTORICAL_CONNECTIONS_NEW_METRICS,
  EXP_CREDITS_NOTICE_SERVICE_CREATION,
  EXP_MICROSURVEY_TEXT_RESPONSES,
];
